import { useEffect } from "react";
import { RouterProvider, Navigate } from "react-router-dom";
import * as Sentry from "@sentry/react";

// Store
import { sentryCreateBrowserRouter } from "@/store/sentry";

// Hooks
import useDetectOS from "@/components/hooks/use-detect-os/use-detect-os";

// Components > Routes
import { routesMap } from "@/components/routes/routes";
import AuthRoute from "@/components/routes/AuthRoute";

// Services
import storageService from "@/services/middleware/storageService";

// Components
import ErrorBoundary from "@/components/exceptions/ErrorBoundary";
import NavigationContainerLoadable from "@/components/navigation-container/NavigationContainer.loadable";
import PrivateRoute from "@/components/routes/private-route/PrivateRoute";
import NetworkStatusOverlay from "@/components/ui/NetworkStatusOverlay/NetworkStatusOverlay";
import OpticksNotifications from "@/components/ui/OpticksNotifications/OpticksNotifications";
import { ModalProvider } from "@/components/ui/OpticksModal/OpticksModal";

// Styles
import "@/styles/index.css";
import "@/styles/fonts.css";
import "@/styles/layouts.css";
import "@/styles/colors.css";

// Pages > Auth
import AccountInactive from "@/pages/auth/views/AccountInactive";
import AccountMissing from "@/pages/auth/views/AccountMissing";
import AppSumoSignup from "@/pages/auth/views/AppSumoSignup";
import EmailVerification from "@/pages/auth/views/EmailVerification";
import EmailVerificationPending from "@/pages/auth/views/EmailVerificationPending";
import Forbidden from "@/pages/auth/views/Forbidden";
import ForgotPassword from "@/pages/auth/views/ForgotPassword";
import Login from "@/pages/auth/views/Login";
import LoginAsUser from "@/pages/auth/views/LoginAsUser";
import Logout from "@/pages/auth/views/Logout";
import PerformanceUnsubscribe from "@/pages/auth/views/performance-unsubscribe/PerformanceUnsubscribe";
import PreRegister from "@/pages/auth/views/PreRegister";
import RelationshipSelector from "@/pages/auth/views/RelationshipSelector";
import ResetPassword from "@/pages/auth/views/ResetPassword";
import Signup from "@/pages/auth/views/Signup";
import LicenseDeactivated from "@/pages/auth/views/LicenseDeactivated";
import SignupPlan from "@/pages/auth/views/SignupPlan";
import TrialEmailVerificationPending from "@/pages/auth/views/TrialEmailVerificationPending";
import GoogleSignup from "@/pages/auth/views/GoogleSignup";

// Pages > Blocked
import Blocked from "@/pages/blocked/Blocked";

// Pages > Intelligence
import OverviewLoadable from "@/pages/overview/root/Overview.loadable";
import AnalyticsLoadable from "@/pages/analytics/Analytics.loadable";
import BenchmarkLoadable from "@/pages/benchmark/root/Benchmark.loadable";
import PerformanceLoadable from "@/pages/performance/root/Performance.loadable";
import AiAssistantLoadable from "@/pages/ai-assistant/AiAssistant.loadable";

// Pages > Reporting
import ReportsLoadable from "@/pages/reports/root/Reports.loadable";
import ReportFormLoadable from "@/pages/reports/root/ReportForm.loadable";
import AlertsLoadable from "@/pages/alerts/Alerts.loadable";
import SessionForensicsLoadable from "@/pages/session-forensics/SessionForensics.loadable";
import ConversionsListLoadable from "@/pages/conversions-list/ConversionsList.loadable";

// Pages > Google
// -- Slim
import GoogleSlimOverviewLoadable from "@/pages/google-ads/views/slim/root/GoogleSlimOverview.loadable";
import GoogleSlimIntegration from "@/pages/google-ads/views/slim/views/integration/GoogleSlimIntegration.view";
// -- Linking
import GoogleAccountIntegrationStepsLoadable from "@/pages/google-ads/views/linking/root/GoogleAccountIntegrationSteps.loadable";
import GoogleAccountIntegrationBannerLoadable from "@/pages/google-ads/views/linking/root/GoogleAccountIntegrationBanner.loadable";
// -- Search & Display
import GoogleSearchDisplayLoadable from "@/pages/google-ads/views/search-display/root/GoogleSearchDisplay.loadable";
// -- Shopping
import GoogleShoppingLoadable from "@/pages/google-ads/views/shopping/root/GoogleShopping.loadable";
// -- Performance Max & Demand Gen
import GooglePmaxLoadable from "@/pages/google-ads/views/pmax/root/GooglePmax.loadable";
// -- Account Settings
import GoogleAccountSettingsLoadable from "@/pages/google-ads/views/account-settings/root/GoogleAccountSettings.loadable";
// -- Global Intelligence
import GoogleGlobalIntelligenceLoadable from "@/pages/google-ads/views/global-intelligence/root/GoogleGlobalIntelligence.loadable";

// Pages > Meta
import MetaOverviewLoadable from "@/pages/meta-ads/root/MetaOverview.loadable";
import MetaIntegration from "@/pages/meta-ads/views/integration/MetaIntegration.view";

// Pages > Microsoft
import MicrosoftOverviewLoadable from "@/pages/microsoft-ads/root/MicrosoftOverview.loadable";
import MicrosoftIntegration from "@/pages/microsoft-ads/views/integration/MicrosoftIntegration.view";

// Pages > TikTok
import TikTokOverviewLoadable from "@/pages/tiktok-ads/root/TikTokOverview.loadable";
import TikTokIntegration from "@/pages/tiktok-ads/views/integration/TikTokIntegration.view";

// Pages > Taboola
import TaboolaOverviewLoadable from "@/pages/taboola/root/TaboolaOverview.loadable";
import TaboolaIntegration from "@/pages/taboola/views/integration/TaboolaIntegration.view";

// Pages > Reddit
import RedditOverviewLoadable from "@/pages/reddit/root/RedditOverview.loadable";
import RedditIntegration from "@/pages/reddit/views/integration/RedditIntegration.view";

// Pages > Channels Center
import ChannelsCenterLoadable from "@/pages/channels-center/ChannelsCenter.loadable";

// Pages > HubSpot
import HubSpotLoadable from "@/pages/hubspot/root/HubSpot.loadable";

// Pages > Leads
import LeadsLoadable from "@/pages/leads/root/Leads.loadable";

// Pages > Sensor Firewall
import CampaignsListLoadable from "@/pages/sensor-firewall/views/campaigns-list/list/CampaignsList.loadable";
import S2SCampaignsListLoadable from "@/pages/sensor-firewall/views/campaigns-list/list-s2s/S2SCampaignsList.loadable";
import TrafficSourcesListLoadable from "@/pages/sensor-firewall/views/traffic-sources-list/list/TrafficSourcesList.loadable";
import OffersListLoadable from "@/pages/sensor-firewall/views/offers-list/list/OffersList.loadable";

// Pages > Setup Center & Integrations
import SetupCenterLoadable from "@/pages/setup-center/SetupCenter.loadable";
import IntegrationsLoadable from "@/pages/setup-center/integrations/Integrations.loadable";

// Pages > Security Options
import APKExclusionListSettingsLoadable from "@/pages/security-options/views/apk-exclusion-list/APKExclusionList.loadable";
import IPSettingsLoadable from "@/pages/security-options/views/ip-settings/IPSettings.loadable";
import JSSettingsLoadable from "@/pages/security-options/views/javascript-settings/JSSettings.loadable";
import PostbackLoadable from "@/pages/security-options/views/postback/Postback.loadable";
import DomainExclusionListLoadable from "@/pages/security-options/views/domain-exclusion-list/DomainExclusionList.loadable";

// Pages > Developer Settings
import DevelopersSettingsLoadable from "@/pages/developers-settings/DevelopersSettings.loadable";

// Pages > Account
import SettingsLoadable from "@/pages/account/views/settings/Settings.loadable";
import SubscriptionCenterLoadable from "@/pages/account/views/subscription-center/SubscriptionCenter.loadable";
import LabelsLoadable from "@/pages/account/views/labels/Labels.loadable";
import UsersLoadable from "@/pages/account/views/users/Users.loadable";
import AccessLoadable from "@/pages/account/views/access/Access.loadable";
import ReferralProgramLoadable from "@/pages/account/views/referral/ReferralProgram.loadable";

const App = () => {
  const { os } = useDetectOS();

  useEffect(() => {
    storageService.setItem("user-os", os === null ? "" : os);
  }, []);

  const router = sentryCreateBrowserRouter(
    [
      {
        path: routesMap.emailVerification.path,
        element: (
          <AuthRoute>
            <EmailVerification />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.auth.login.path,
        element: (
          <AuthRoute>
            <Login />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.accountInactive.path,
        element: (
          <AuthRoute>
            <AccountInactive />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.accountMissing.path,
        element: (
          <AuthRoute>
            <AccountMissing />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.licenseDeactivated.path,
        element: (
          <AuthRoute>
            <LicenseDeactivated />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.auth.loginAsUser.path,
        element: (
          <AuthRoute>
            <LoginAsUser />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.auth.preregister.path,
        element: (
          <AuthRoute>
            <PreRegister />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.auth.signup.path,
        element: (
          <AuthRoute>
            <Signup />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.auth.signupPlan.path,
        element: (
          <AuthRoute>
            <SignupPlan />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.auth.appsumoSignup.path,
        element: (
          <AuthRoute>
            <AppSumoSignup />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.auth.forgotPassword.path,
        element: (
          <AuthRoute>
            <ForgotPassword />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.auth.emailVerificationPending.path,
        element: (
          <AuthRoute>
            <EmailVerificationPending />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.auth.trialEmailVerificationPending.path,
        element: (
          <AuthRoute>
            <TrialEmailVerificationPending />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.auth.resetPassword.path,
        element: (
          <AuthRoute>
            <ResetPassword />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.auth.googleSignup.path,
        element: (
          <AuthRoute>
            <GoogleSignup />
          </AuthRoute>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.role.path,
        element: <RelationshipSelector />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.logout.path,
        element: <Logout />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.blocked.path,
        element: <Blocked />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.forbidden.path,
        element: <Forbidden />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: routesMap.unsubscribe.path,
        element: <PerformanceUnsubscribe />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: "/",
        element: <PrivateRoute />,
        errorElement: <ErrorBoundary />,
        children: [
          {
            element: <NavigationContainerLoadable />,
            children: [
              {
                index: true,
                element: <PrivateRoute />,
              },
              {
                path: routesMap.intelligence.analytics.path,
                element: <AnalyticsLoadable />,
              },
              {
                path: routesMap.intelligence.overview.path,
                element: <OverviewLoadable />,
              },
              {
                path: routesMap.intelligence.performance.path,
                element: <PerformanceLoadable />,
              },
              {
                path: routesMap.intelligence.benchmark.path,
                element: <BenchmarkLoadable />,
              },
              {
                path: routesMap.intelligence.reports.list.path,
                element: <ReportsLoadable />,
              },
              {
                path: routesMap.intelligence.reports.form.path,
                element: <ReportFormLoadable />,
              },
              {
                path: routesMap.intelligence.forensics.path,
                element: <SessionForensicsLoadable />,
              },
              {
                path: routesMap.intelligence.conversions.path,
                element: <ConversionsListLoadable />,
              },
              {
                path: routesMap.intelligence.alerts.path,
                element: <AlertsLoadable />,
              },
              {
                path: routesMap.integration.home.path,
                element: <IntegrationsLoadable />,
              },
              {
                path: routesMap.integration.google.steps.path,
                element: <GoogleSlimIntegration />,
              },
              {
                path: routesMap.integration.google.unlinked.path,
                element: <GoogleAccountIntegrationBannerLoadable />,
              },
              {
                path: routesMap.integration.google.linking.path,
                element: <GoogleAccountIntegrationStepsLoadable />,
              },
              {
                path: routesMap.integration.google.slim.path,
                element: <GoogleSlimOverviewLoadable />,
              },
              {
                path: routesMap.integration.google.searchdisplay.path,
                element: <GoogleSearchDisplayLoadable />,
              },
              {
                path: routesMap.integration.google.shopping.path,
                element: <GoogleShoppingLoadable />,
              },
              {
                path: routesMap.integration.google.pmax.path,
                element: <GooglePmaxLoadable />,
              },
              {
                path: routesMap.integration.google.accountSettings.path,
                element: <GoogleAccountSettingsLoadable />,
              },
              {
                path: routesMap.integration.google.globalIntelligence.path,
                element: <GoogleGlobalIntelligenceLoadable />,
              },
              {
                path: routesMap.integration.meta.linking.path,
                element: <MetaIntegration />,
              },
              {
                path: routesMap.integration.meta.overview.path,
                element: <MetaOverviewLoadable />,
              },
              {
                path: routesMap.integration.microsoft.linking.path,
                element: <MicrosoftIntegration />,
              },
              {
                path: routesMap.integration.microsoft.overview.path,
                element: <MicrosoftOverviewLoadable />,
              },
              {
                path: routesMap.integration.tiktok.linking.path,
                element: <TikTokIntegration />,
              },
              {
                path: routesMap.integration.tiktok.overview.path,
                element: <TikTokOverviewLoadable />,
              },
              {
                path: routesMap.integration.taboola.linking.path,
                element: <TaboolaIntegration />,
              },
              {
                path: routesMap.integration.taboola.overview.path,
                element: <TaboolaOverviewLoadable />,
              },
              {
                path: routesMap.integration.reddit.linking.path,
                element: <RedditIntegration />,
              },
              {
                path: routesMap.integration.reddit.overview.path,
                element: <RedditOverviewLoadable />,
              },
              {
                path: routesMap.integration.hubspot.overview.path,
                element: <HubSpotLoadable />,
              },
              {
                path: routesMap.integration.channelsCenter.path,
                element: <ChannelsCenterLoadable />,
              },
              {
                path: routesMap.setup.setupCenter.path,
                element: <SetupCenterLoadable />,
              },
              {
                path: routesMap.intelligence.aiAssistant.path,
                element: <AiAssistantLoadable />,
              },
              {
                path: routesMap.intelligence.leads.path,
                element: <LeadsLoadable />,
              },
              {
                path: routesMap.setup.sensorFirewall.list.path,
                element: <CampaignsListLoadable />,
              },
              {
                path: routesMap.setup.trafficSource.list.path,
                element: <TrafficSourcesListLoadable />,
              },
              {
                path: routesMap.setup.offer.list.path,
                element: <OffersListLoadable />,
              },
              {
                path: routesMap.setup.sensorS2S.list.path,
                element: <S2SCampaignsListLoadable />,
              },
              {
                path: routesMap.setup.security.apkSecurity.path,
                element: <APKExclusionListSettingsLoadable />,
              },
              {
                path: routesMap.setup.security.ipSettings.path,
                element: <IPSettingsLoadable />,
              },
              {
                path: routesMap.setup.security.jsSettings.path,
                element: <JSSettingsLoadable />,
              },
              {
                path: routesMap.setup.security.postbackSecurity.path,
                element: <PostbackLoadable />,
              },
              {
                path: routesMap.setup.security.domainSecurity.path,
                element: <DomainExclusionListLoadable />,
              },
              {
                path: routesMap.account.users.path,
                element: <UsersLoadable />,
              },
              {
                path: routesMap.account.settings.path,
                element: <SettingsLoadable />,
              },
              {
                path: routesMap.account.labels.path,
                element: <LabelsLoadable />,
              },
              {
                path: routesMap.account.access.path,
                element: <AccessLoadable />,
              },
              {
                path: routesMap.account.subscriptionCenter.path,
                element: <SubscriptionCenterLoadable />,
              },
              {
                path: routesMap.account.referral.path,
                element: <ReferralProgramLoadable />,
              },
              {
                path: routesMap.developersSettings.path,
                element: <DevelopersSettingsLoadable />,
              },
              {
                path: "*",
                element: <Navigate to="/" replace />,
              },
            ],
          },
        ],
      },
    ],
    {
      future: {
        v7_relativeSplatPath: true,
      },
    }
  );

  return (
    <>
      <OpticksNotifications />
      <NetworkStatusOverlay />
      <ModalProvider>
        <RouterProvider
          router={router}
          future={{
            v7_startTransition: true,
          }}
        />
      </ModalProvider>
    </>
  );
};

export default Sentry.withProfiler(App);
